/**
 * Decoded lesson images for the immersive panel.
 *
 * `drawLessonPanel` is a synchronous, pure draw — both players call it on every
 * state change and on every animation frame — but an image cannot be drawn onto
 * a canvas until it has loaded. So loading happens here: the renderer asks for
 * an image and gets either the decoded bitmap or null, and is told once, later,
 * that a redraw is worth doing.
 *
 * Crossorigin is set because the canvas is uploaded as a WebGL texture: a
 * tainted canvas cannot be, and the panel would go blank rather than just miss
 * one picture.
 */

type Entry =
  | { state: 'loading' }
  | { state: 'ready'; image: HTMLImageElement }
  | { state: 'failed' };

const cache = new Map<string, Entry>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a broken listener must not stop the others being told */
    }
  });
}

/**
 * Subscribe to "an image finished loading", so the player can redraw the panel.
 * Returns an unsubscribe function.
 */
export function onLessonImageReady(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The decoded image for a URL, or null while it loads (or if it never will).
 * Safe to call on every frame: the work happens once per URL.
 */
export function getLessonImage(url: string | undefined | null): HTMLImageElement | null {
  const key = String(url || '').trim();
  if (!key || typeof Image === 'undefined') return null;

  const entry = cache.get(key);
  if (entry) return entry.state === 'ready' ? entry.image : null;

  cache.set(key, { state: 'loading' });
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    cache.set(key, { state: 'ready', image });
    notify();
  };
  image.onerror = () => {
    cache.set(key, { state: 'failed' });
    notify();
  };
  image.src = key;
  return null;
}

/** True while a URL is still being fetched, so the panel can say "Loading…". */
export function isLessonImageLoading(url: string | undefined | null): boolean {
  const key = String(url || '').trim();
  return cache.get(key)?.state === 'loading';
}
